import findReadWriteRecipient from '..//actions/findReadWriteRecipient';
import addMentionAddedRecipient from '../actions/addMentionAddedRecipient';
import addMentionTuple from '../actions/addMentionTuple';
import removeMentionAddedRecipient from '../actions/removeMentionAddedRecipient';
import removeMentionTuple from '../actions/removeMentionTuple';
import setIsSearching from '../actions/setIsSearching';
import setIsSuggesting from '../actions/setIsSuggesting';
import setSuggestedContacts from '../actions/setSuggestedContacts';
import shiftAtMentionsHighlight from '../actions/shiftAtMentionsHighlight';
import { AT_MENTION_ELEMENT_ID_PREFIX } from '../constants';
import type AtMentionsViewState from '../store/schema/AtMentionsViewState';
import { logUsage } from 'owa-analytics';
import type { PluginViewStateAdapter } from 'owa-editor';
import type FindRecipientPersonaType from 'owa-recipient-types/lib/types/FindRecipientPersonaType';
import type ReadWriteRecipientViewState from 'owa-recipient-types/lib/types/ReadWriteRecipientViewState';
import compareFindRecipientPersonaType from 'owa-recipient-common/lib/utils/compareFindRecipientPersonaType';
import getDisplayNameFromFindRecipientPersonaType from 'owa-recipient-common/lib/utils/getDisplayNameFromFindRecipientPersonaType';
import type { IEditor, PickerDataProvider } from 'roosterjs-editor-types';
import setTargetPoint from 'owa-editor-picker-plugin/lib/actions/setTargetPoint';

const AT_MENTION_SEARCH_DELAY = 300;
import styles from '../components/MentionComposeStyles.scss';

export type WellModifierFunctionType = (persona: FindRecipientPersonaType) => void;

// Note:
// setIsSuggesting is a satchel action for this data provider
// setIsSuggestingCallback is the callback method from the Picker Plugin.

export class MentionPluginDataProvider implements PickerDataProvider {
    private timer: NodeJS.Timer;
    private commitMention: (element: HTMLElement) => void;
    private setIsSuggestingCallback: (isSuggesting: boolean) => void;
    public editor: IEditor | undefined;

    constructor(
        private adapter: PluginViewStateAdapter<AtMentionsViewState>,
        private addSelectedPersonaToWell: WellModifierFunctionType,
        private removePersonaFromWell: WellModifierFunctionType,
        private onRecipientAdded: () => void,
        private canModifyRecipients: boolean,
        private getAllRecipients: () => ReadWriteRecipientViewState[]
    ) {}

    onInitalize(
        commitMentionCallback: (element: HTMLElement) => void,
        isSuggestingCallback: (isSuggesting: boolean) => void,
        editor?: IEditor
    ) {
        this.commitMention = commitMentionCallback;
        this.setIsSuggestingCallback = isSuggestingCallback;
        this.editor = editor;
    }

    onDispose() {
        clearTimeout(this.timer);
        this.timer = null;
    }

    onIsSuggestingChanged = (isSuggesting: boolean) => {
        const viewState = this.adapter();

        setIsSuggesting(viewState, isSuggesting);

        if (!isSuggesting) {
            setTargetPoint(this.adapter(), null /*targetPoint*/, 0 /*buffer/Zone*/);
        } else if (!viewState.queryString) {
            setSuggestedContacts(viewState, this.getAllRecipients());
        }
    };

    queryStringUpdated = (queryString: string) => {
        const viewState = this.adapter();

        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        if (queryString) {
            setIsSearching(viewState, true, queryString);
            this.timer = setTimeout(() => {
                findReadWriteRecipient(viewState, queryString, false, this.getAllRecipients());
                this.timer = null;
            }, AT_MENTION_SEARCH_DELAY);
        } else {
            setSuggestedContacts(viewState, this.getAllRecipients());
        }
    };

    selectOption = () => {
        const viewState = this.adapter();

        if (!viewState.isSearching && viewState.findResultSet.length == 0) {
            this.setIsSuggestingCallback(false);
        } else if (viewState.selectedRecipientIndex == viewState.findResultSet.length) {
            findReadWriteRecipient(viewState, viewState.queryString, true, this.getAllRecipients());
        } else {
            this.addRecipient();
        }
    };

    shiftHighlight = (isDown: boolean) => {
        shiftAtMentionsHighlight(this.adapter(), isDown, this.editor?.getDocument());
    };

    onRemove = (nodeRemoved: Node, isBackspace: boolean) => {
        if (isBackspace && nodeRemoved.textContent.split(' ').length > 1) {
            return this.deleteLastSegment(nodeRemoved);
        } else {
            this.deleteAtMention(nodeRemoved);
            return null;
        }
    };

    setCursorPoint = (targetPoint: { x: number; y: number }, buffer: number) => {
        setTargetPoint(this.adapter(), targetPoint, buffer);
    };

    getSelectedIndex = (): number => {
        return this.adapter().selectedRecipientIndex;
    };

    onScroll = () => {
        if (this.adapter().isSuggesting) {
            this.setIsSuggestingCallback(false);
        }
    };

    addRecipient = (recipient: FindRecipientPersonaType = null) => {
        const viewState = this.adapter();

        if (!recipient) {
            recipient = viewState.findResultSet[viewState.selectedRecipientIndex];
        }

        this.commitMention(this.createInlineFromPersona(recipient));

        if (!this.doesWellContainRecipient(recipient) && this.canModifyRecipients) {
            recipient = recipient ?? viewState.findResultSet[viewState.selectedRecipientIndex];
            this.addSelectedPersonaToWell(recipient);
            if (recipient.EmailAddress) {
                addMentionAddedRecipient(viewState, recipient);
            }
        }

        setIsSuggesting(viewState, false);

        if (this.onRecipientAdded) {
            this.onRecipientAdded();
        }

        logUsage('MentionsAdded', undefined /* customData */, { isCore: true });
    };

    private doesWellContainRecipient(recipientToCheck?: FindRecipientPersonaType) {
        const viewState = this.adapter();

        if (!recipientToCheck) {
            recipientToCheck = viewState.findResultSet[viewState.selectedRecipientIndex];
        }
        const filterFunc = (value: ReadWriteRecipientViewState) => {
            return compareFindRecipientPersonaType(value.persona, recipientToCheck);
        };

        return this.getAllRecipients().some(filterFunc);
    }

    private createInlineFromPersona(targetRecipient: FindRecipientPersonaType): HTMLAnchorElement {
        const anchorElement = document.createElement('a');
        const atMentionId = this.generateAtMentionId();
        const targetName = getDisplayNameFromFindRecipientPersonaType(targetRecipient);
        anchorElement.id = atMentionId;
        anchorElement.innerText = '@' + targetName;

        // We add both our styling for React, as well as the hard coded styling for MVVM.
        anchorElement.className = styles.mentionsCompose + ' mention ms-bgc-nlr ms-fcl-b';

        const email = targetRecipient.EmailAddress.EmailAddress;
        anchorElement.href = 'mailto:' + email;

        addMentionTuple(this.adapter(), atMentionId, targetRecipient);
        return anchorElement;
    }

    private generateAtMentionId(): string {
        return AT_MENTION_ELEMENT_ID_PREFIX + Math.round(Math.random() * 1000000).toString();
    }

    private deleteLastSegment(node: Node) {
        const splitContents = node.textContent.split(' ');
        splitContents.pop();
        node.textContent = splitContents.join(' ').trim();
        return node;
    }

    private getIdValue(node: Node): string {
        const element = node as Element;
        return element.attributes && element.attributes.getNamedItem('id')
            ? (element.attributes.getNamedItem('id').value as string)
            : null;
    }

    private deleteAtMention(node: Node) {
        const nodeId = this.getIdValue(node);
        const viewState = this.adapter();
        const { atMentionTuples, mentionsAddedRecipients } = viewState;

        if (atMentionTuples[nodeId]) {
            if (atMentionTuples[nodeId].EmailAddress) {
                const indexToRemove = mentionsAddedRecipients.indexOf(
                    atMentionTuples[nodeId].EmailAddress.EmailAddress
                );
                if (indexToRemove > -1) {
                    // If someone @mentioned someone multiple times, we should not remove until it's the last one.
                    const duplicated = Object.keys(atMentionTuples).some((key): boolean => {
                        if (key != nodeId && atMentionTuples[key]) {
                            return (
                                atMentionTuples[key].EmailAddress &&
                                atMentionTuples[key].EmailAddress.EmailAddress ==
                                    atMentionTuples[nodeId].EmailAddress.EmailAddress
                            );
                        } else {
                            return false;
                        }
                    });
                    if (!duplicated && this.canModifyRecipients) {
                        this.removePersonaFromWell(atMentionTuples[nodeId]);
                        removeMentionAddedRecipient(viewState, indexToRemove);
                    }
                }
            }
            removeMentionTuple(viewState, nodeId);
        }
    }
}
